import React, { useState, useEffect } from "react";
import { Avatar, Card, Col, Input, Row, Form, Modal } from "antd";
import http from "../../../../services/http";
import { BounceLoader } from "react-spinners";
import { ProfileFormSkeleton } from "../../../../components/page-skeleton";
import {
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import ChangePassword from "./change-password";
import SubscriptionDetail from "./subscription-detail";
import { getInitials } from "../../../../utils";
import { useSelector } from "react-redux";
import { PROFESSIONAL_ROLE } from "../../../../constant";
import { Close } from "@mui/icons-material";
import Cookie from "js-cookie";
import "./styles.scss";

function MyProfile() {
  // STATES
  const [profileLoader, setProfileLoader] = useState(true);
  const [changePasswordPreview, setchangePasswordPreview] = useState(false);
  const [updatePreview, setupdatePreview] = useState(false);
  const [updateLoader, setupdateLoader] = useState(false);
  const [logoInitial, setLogoInitial] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [email, setEmail] = useState("");
  const [deleteAccountPreview, setDeleteAccountPreview] = useState(false);
  const [deleteAccountLoader, setDeleteAccountLoader] = useState(false);

  // REDUX DATA
  const user = useSelector((state) => state.UserReducer.profile);

  // CONST VALS
  const [form] = Form.useForm();
  const hasUsername = logoInitial && logoInitial.length > 1;
  const isSocialUser = user?.social_user;
  // TODO: uncomment this when we restrict professional users from accessing enterprise features
  // const enterpriseUser = user?.role === ENTERPRISE_ROLE;
  const enterpriseUser = user?.organization;
  const showSubscriptionCard = user?.role === PROFESSIONAL_ROLE;
  const displayName = logoInitial || "Your profile";
  const planStatusKey = user?.is_subscribed
    ? "active"
    : user?.free_trial
    ? "free-trial"
    : "inactive";
  const planStatusLabel =
    planStatusKey === "active"
      ? "Active"
      : planStatusKey === "free-trial"
      ? "Free trial"
      : "Inactive";

  // HELPERS
  const getProfileHelper = async () => {
    setProfileLoader(true);
    http
      .get(`user-profile`)
      .then(({ data }) => {
        setLogoInitial(data?.user?.username);
        setProfilePic(data?.user?.profile_picture);
        setEmail(data?.user?.email || "");
        form.setFieldsValue(data.user);
      })
      .finally(() => setProfileLoader(false));
  };

  // HANDLERS
  const changePasswordHandler = () => {
    setchangePasswordPreview(!changePasswordPreview);
  };
  const handleSubmit = (values) => {
    setupdateLoader(true);
    http
      .put(`user-profile`, values)
      .then(() => {
        toast.success("Profile updated successfully");
        setLogoInitial(values.first_name + " " + values.last_name);
        setupdatePreview(false);
      })
      .catch(() => toast.error("Error updating profile"))
      .finally(() => setupdateLoader(false));
  };
  const deleteAccountHandler = () => {
    if (deleteAccountLoader) return;
    setDeleteAccountLoader(true);
    http
      .delete("delete-account")
      .then(() => {
        toast.success("Account deleted successfully");
        Object.keys(Cookie.get()).forEach((cookieName) => {
          Cookie.remove(cookieName);
        });
        window.location.replace("/");
      })
      .catch(() => {
        toast.error("Unable to delete account");
      })
      .finally(() => {
        setDeleteAccountLoader(false);
        setDeleteAccountPreview(false);
      });
  };

  // HOOKS
  useEffect(() => {
    getProfileHelper();
  }, []);

  return (
    <div className="profile-wrapper">
      <header className="profile-hero">
        <div className="profile-hero__main">
          <div className="profile-hero__avatar-wrap">
            {profileLoader ? (
              <div className="profile-hero__avatar profile-hero__avatar--skeleton" />
            ) : (
              <Avatar
                src={profilePic}
                className={`profile-hero__avatar ${!hasUsername ? "large" : ""}`}
                shape="round"
              >
                {hasUsername ? getInitials(logoInitial) : <UserOutlined />}
              </Avatar>
            )}
          </div>
          <div className="profile-hero__meta">
            <p className="profile-hero__eyebrow">Account settings</p>
            <h2 className="profile-hero__title">
              {profileLoader ? "Loading profile…" : displayName}
            </h2>
            {!profileLoader && email && <p className="profile-hero__email">{email}</p>}
            <p className="profile-hero__subtitle">
              Manage your personal details, security, and subscription.
            </p>
          </div>
        </div>
        {showSubscriptionCard && (
          <div className="profile-hero__plan-status">
            <span className="profile-hero__plan-label">Plan status:</span>
            <span
              className={`subscription-card__status subscription-card__status--${planStatusKey}`}
            >
              {planStatusLabel}
            </span>
          </div>
        )}
      </header>

      <Card className="card-box profile-section">
        <div className="profile-section__header">
          <div className="profile-section__icon">
            <UserOutlined />
          </div>
          <div>
            <h3 className="card-title">Personal information</h3>
            <p className="profile-section__desc">
              Update the name shown across Conceptor.
            </p>
          </div>
        </div>
        {profileLoader ? (
          <ProfileFormSkeleton />
        ) : (
          <Form
            form={form}
            onFinish={handleSubmit}
            onFieldsChange={() => setupdatePreview(true)}
            className="profile-form"
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} md={12} lg={enterpriseUser ? 6 : 8}>
                <label htmlFor="firstName">First name</label>
                <Form.Item
                  name="first_name"
                  rules={[
                    {
                      required: true,
                      message: "First name is required",
                    },
                    {
                      whitespace: true,
                      message: "Invalid first name",
                    },
                    {
                      max: 30,
                      message: "First name must be less than 30 characters",
                    },
                  ]}
                >
                  <Input type="text" id="firstName" placeholder="First name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={enterpriseUser ? 6 : 8}>
                <label htmlFor="lastName">Last name</label>
                <Form.Item
                  name="last_name"
                  rules={[
                    {
                      required: true,
                      message: "Last name is required",
                    },
                    {
                      whitespace: true,
                      message: "Invalid last name",
                    },
                    {
                      max: 30,
                      message: "Last name must be less than 30 characters",
                    },
                  ]}
                >
                  <Input type="text" id="lastName" placeholder="Last name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={enterpriseUser ? 6 : 8}>
                <label htmlFor="email">Email address</label>
                <Form.Item name="email">
                  <Input type="text" id="email" disabled />
                </Form.Item>
              </Col>
              {enterpriseUser && (
                <Col xs={24} md={12} lg={6}>
                  <label htmlFor="organization">Organization</label>
                  <Form.Item name="organization">
                    <Input type="text" id="organization" disabled />
                  </Form.Item>
                </Col>
              )}
            </Row>
            <div className="action-box">
              <button type="submit" disabled={!updatePreview}>
                {updateLoader ? <BounceLoader color="#141414" size={15} /> : "Save changes"}
              </button>
            </div>
          </Form>
        )}
      </Card>
      {showSubscriptionCard && <SubscriptionDetail />}

      {!isSocialUser && (
        <Card className="card-box profile-section password-card">
          <div className="profile-section__header">
            <div className="profile-section__icon">
              <LockOutlined />
            </div>
            <div className="profile-section__copy">
              <h3 className="card-title">Password</h3>
              <p className="profile-section__desc">
                Keep your account secure with a strong password.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="password-input"
            onClick={changePasswordHandler}
            aria-label="Change password"
          >
            <span className="password-input__dots">••••••••••••</span>
            <span className="password-input__hint">Click to update</span>
          </button>
        </Card>
      )}

      <Card className="card-box profile-section delete-account-card">
        <div className="profile-section__header">
          <div className="profile-section__icon profile-section__icon--danger">
            <DeleteOutlined />
          </div>
          <div className="profile-section__copy">
            <h3 className="card-title">Delete account</h3>
            <p className="profile-section__desc">
              Permanently remove your account and all associated data. This cannot be
              undone.
            </p>
          </div>
          <button
            type="button"
            className="delete-account-btn"
            onClick={() => setDeleteAccountPreview(true)}
          >
            <DeleteOutlined />
            Delete account
          </button>
        </div>
      </Card>

      <Modal
        visible={deleteAccountPreview}
        onCancel={() => setDeleteAccountPreview(false)}
        closable={false}
        footer={null}
        className="delete-account-modal"
      >
        <button className="close-btn" onClick={() => setDeleteAccountPreview(false)}>
          <Close />
        </button>
        <h2>Delete account</h2>
        <p>
          Are you sure you want to delete your account? This action is permanent and
          cannot be undone.
        </p>
        <div className="action-box">
          <button type="button" onClick={() => setDeleteAccountPreview(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="submit delete-btn"
            disabled={deleteAccountLoader}
            onClick={deleteAccountHandler}
          >
            {deleteAccountLoader ? <BounceLoader color="#fff" size={15} /> : "Delete"}
          </button>
        </div>
      </Modal>
      <ChangePassword
        preview={changePasswordPreview}
        previewHandler={changePasswordHandler}
      />
    </div>
  );
}

export default MyProfile;
